import React, { createContext, useContext, useMemo, useState } from 'react';
import { isApproximate } from '../lib/geo';
import { geocodePlace } from '../services/storeSearch';

const LocationContext = createContext(null);

// Where the user is — asked for once, when they need shops, and not before.
//
// There is deliberately NO background tracking here. No watchPosition, no
// polling, no request on app start. The permission prompt appears at the
// moment the store section needs an answer, which is the only moment the user
// can judge whether granting it is worth it. If that ever changes, it should
// be an argued decision and not a quiet import.
//
// A coarse fix is accepted as it is. We never re-ask for precision: the
// distances are labelled approximate instead, and the user can correct their
// location by hand whenever they like.

const IDLE = {
  status: 'idle', // idle | prompting | ready | denied | unavailable
  coords: null, // { lat, lon, accuracyMeters, source, label, countryCode }
  error: null,
};

// A coarse fix is fine; a slow one is not, since the user is waiting on a
// screen that says nothing until it resolves. maximumAge lets a fix from a few
// minutes ago answer instantly — good enough for "which shop is nearest".
const GEO_OPTIONS = {
  enableHighAccuracy: false,
  timeout: 10000,
  maximumAge: 5 * 60 * 1000,
};

export function LocationProvider({ children }) {
  const [state, setState] = useState(IDLE);

  const value = useMemo(
    () => ({
      ...state,
      hasLocation: Boolean(state.coords),
      isApproximate: isApproximate(state.coords),

      requestDeviceLocation: () => {
        if (typeof navigator === 'undefined' || !navigator.geolocation) {
          setState({ ...IDLE, status: 'unavailable' });
          return;
        }

        setState((prev) => ({ ...prev, status: 'prompting', error: null }));

        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude, accuracy } = position.coords;
            setState({
              status: 'ready',
              coords: {
                lat: latitude,
                lon: longitude,
                accuracyMeters: typeof accuracy === 'number' ? accuracy : null,
                source: 'device',
                label: null,
                countryCode: null,
              },
              error: null,
            });
          },
          (error) => {
            // Denial is a decision, not a fault. It routes to manual entry
            // with no scolding and no second prompt.
            const denied = error?.code === 1;
            setState({
              ...IDLE,
              status: denied ? 'denied' : 'unavailable',
              error: denied ? null : 'Your device could not provide a location.',
            });
          },
          GEO_OPTIONS
        );
      },

      // The one manual fallback: a city or postal code plus a country. The
      // country is asked for rather than assumed — the same city name exists
      // in a dozen of them.
      setManualLocation: async ({ query, countryCode }) => {
        setState((prev) => ({ ...prev, status: 'prompting', error: null }));
        try {
          const place = await geocodePlace({ query, countryCode });
          if (!place) {
            setState((prev) => ({
              ...prev,
              status: prev.coords ? 'ready' : 'denied',
              error: `We couldn't find "${query}" in ${countryCode}.`,
            }));
            return false;
          }
          setState({
            status: 'ready',
            coords: {
              lat: place.lat,
              lon: place.lon,
              // A geocoded place is a centroid, never a position. Left null so
              // nothing can print a false precision figure; `isApproximate`
              // keys off the source instead.
              accuracyMeters: null,
              source: 'manual',
              label: place.label,
              countryCode: place.countryCode ?? countryCode,
            },
            error: null,
          });
          return true;
        } catch (error) {
          setState((prev) => ({
            ...prev,
            status: prev.coords ? 'ready' : 'denied',
            error:
              error?.reason === 'unconfigured'
                ? 'Store lookup is not configured on the server.'
                : "We couldn't look that up just now.",
          }));
          return false;
        }
      },

      clearLocation: () => setState(IDLE),
    }),
    [state]
  );

  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
}

export function useLocation() {
  const context = useContext(LocationContext);
  if (!context) throw new Error('useLocation must be used inside <LocationProvider>');
  return context;
}
